import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiUser, FiLock, FiArrowRightCircle } from "react-icons/fi";
import { Building2, LineChart, Wallet } from "lucide-react";

const PortalPopup = ({ user = {}, onClose }) => {
  const portals = user?.portal || [];

  const portalIcons = {
    admin: <FiLock className="text-purple-600 text-xl" />,
    hrms: <Building2 className="text-blue-500 w-6 h-6" />,
    sales: <LineChart className="text-green-500 w-6 h-6" />,
    payroll: <Wallet className="text-purple-500 w-6 h-6" />,
    attendance: <FiUser className="text-green-600 text-xl" />,
    training: <FiUser className="text-orange-600 text-xl" />,
    recruiter: <FiUser className="text-indigo-600 text-xl" />,
  };

  const navigateToPortal = (portal) => {
    // Define portal URLs
    const portalUrls = {
      hrms: "/hrms/dashboard",
      admin: "/admin/dashboard",
      payroll: "/payroll/dashboard",
      sales: "/sales/dashboard",
      attendance: "/attendance/dashboard",
      training: "/training/dashboard",
      recruiter: "/recruiter/dashboard",
    };

    const url = portalUrls[portal];
    if (url) {
      window.location.href = url;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 backdrop-blur-sm cursor-pointer"
      >
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.7, opacity: 0 }}
          className="relative w-full max-w-lg bg-white/90 backdrop-blur-xl shadow-2xl rounded-2xl p-6 border border-gray-200"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-black transition"
          >
            <FiX size={22} />
          </button>

          {/* Avatar */}
          <div className="flex flex-col items-center gap-3 mb-4">
            {user?.profileImage ? (
              <img
                src={user.profileImage}
                alt="avatar"
                className="w-20 h-20 rounded-full shadow-md object-cover ring-2 ring-pink-300"
              />
            ) : (
              <div className="capitalize w-20 h-20 rounded-full bg-pink-600 text-white text-3xl font-semibold flex items-center justify-center shadow-md ring-2 ring-pink-300">
                {user?.firstName?.charAt(0) || "U"}
              </div>
            )}

            <h2 className="text-xl font-bold text-gray-800 capitalize">
              {user?.firstName + " " + user?.lastName || "User"}
            </h2>
            <p className="text-sm text-gray-500">{user?.email}</p>
          </div>

          {/* Portals List */}
          <div className="grid grid-cols-1 gap-3 mt-4">
            {portals.length > 0 ? (
              portals.map((p, i) => (
                <motion.div
                  key={i}
                  onClick={() => {
                    navigateToPortal(p);
                  }}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center justify-between p-3 rounded-xl border bg-white hover:bg-gray-50 transition shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    {portalIcons[p] || (
                      <FiUser className="text-gray-500 text-xl" />
                    )}
                    <p className="uppercase font-medium text-gray-700">{p}</p>
                  </div>

                  <FiArrowRightCircle className="text-gray-500 text-xl hover:text-black transition cursor-pointer" />
                </motion.div>
              ))
            ) : (
              <p className="text-center text-gray-500 py-4">
                No portals assigned.
              </p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PortalPopup;